import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/firestore';
import { useUser } from '../context/userContext';
import { IoCheckmarkCircleSharp } from 'react-icons/io5';

const friendsRewards = [
  { title: 'Invite 10 friends', referralsRequired: 10, bonusAward: 50000 },
  { title: 'Invite 25 friends', referralsRequired: 25, bonusAward: 150000 },
];

const RewardButton = ({ isClaimable, onClick, children }) => (
  <button
    disabled={!isClaimable}
    onClick={onClick}
    className={`${
      isClaimable ? 'bg-btn text-[#000]' : 'bg-[#1f2023] text-[#888]'
    } w-fit py-[10px] rounded-[30px] px-5 font-semibold ease-in duration-200`}
    aria-label={isClaimable ? 'Claim reward' : 'Reward not claimable yet'}
  >
    {children}
  </button>
);

RewardButton.propTypes = {
  isClaimable: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const Modal = ({ isOpen, onClose, children }) => (
  <div
    className={`${
      isOpen ? 'visible' : 'invisible'
    } fixed top-[-12px] bottom-0 left-0 z-40 right-0 h-[100vh] bg-[#00000042] flex justify-center items-center backdrop-blur-[6px] px-4`}
    role="dialog"
    aria-modal="true"
    aria-labelledby="modal-title"
  >
    <div
      className={`${
        isOpen ? 'opacity-100 mt-0' : 'opacity-0 mt-[100px]'
      } w-full bg-modal relative rounded-[16px] flex flex-col justify-center p-8 ease-in duration-300 transition-all`}
    >
      {children}
      <div className="w-full flex justify-center">
        <button
          onClick={onClose}
          className="bg-btn4 w-fit py-[10px] px-6 flex items-center justify-center text-center rounded-[12px] font-medium text-[16px]"
        >
          Continue to next
        </button>
      </div>
    </div>
  </div>
);

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const RefRewards = () => {
  const { 
    referrals, 
    balance, 
    setBalance, 
    id, 
    setTaskPoints, 
    claimedReferralRewards, 
    setClaimedReferralRewards 
  } = useUser();
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [congrats, setCongrats] = useState(false);
  const [congratsTimeoutId, setCongratsTimeoutId] = useState(null);

  useEffect(() => {
    return () => {
      if (congratsTimeoutId) {
        clearTimeout(congratsTimeoutId);
      }
    };
  }, [congratsTimeoutId]);

  const formatNumber = useCallback((number) => {
    if (number == null || isNaN(number)) {
      return '';
    }
  
    if (number >= 1000000) {
      return (number / 1000000).toFixed() + 'M';
    } else if (number >= 100000) {
      return (number / 1000).toFixed(0) + 'K';
    } 
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }, []);

  const handleClaim = useCallback(async (reward) => {
    if (referrals.length >= reward.referralsRequired && !claimedReferralRewards.includes(reward.title)) {
      const newBalance = balance + reward.bonusAward;
      try {
        const userRef = doc(db, 'telegramUsers', id);
        await updateDoc(userRef, {
          balance: newBalance,
          claimedReferralRewards: [...claimedReferralRewards, reward.title],
          taskPoints: balance + reward.bonusAward,
        });

        setBalance(newBalance);
        setTaskPoints(prevTaskPoints => prevTaskPoints + reward.bonusAward);
        setClaimedReferralRewards(prev => [...prev, reward.title]);
        
        setModalMessage(
          <div className="w-full flex justify-center flex-col items-center space-y-3">
            <div className="w-full items-center justify-center flex flex-col space-y-2">
              <IoCheckmarkCircleSharp size={32} className="text-accent" />
              <p className="font-medium text-center">Great job!</p>
            </div>
            <h3 id="modal-title" className="font-medium text-[20px] text-[#ffffff] pt-2 pb-2">
              <span className="text-accent">+{formatNumber(reward.bonusAward)}</span> $TFlash CLAIMED
            </h3>
            <p className="pb-6 text-[#bfbfbf] text-[15px] w-full text-center">
              Keep inviting friends to unlock new rewards! Amazing things await!
            </p>
          </div>
        );

        setModalOpen(true);
        setCongrats(true);

        const timeoutId = setTimeout(() => {
          setCongrats(false);
        }, 4000);
        setCongratsTimeoutId(timeoutId);
      } catch (error) {
        console.error('Error claiming referral reward:', error);
        setModalMessage(
          <div className="text-center text-red-500">
            Failed to claim reward. Please try again later.
          </div>
        );
        setModalOpen(true);
      }
    } else {
      setModalMessage(
        <div className="text-center">
          You have already claimed this referral reward or do not meet the requirements.
        </div>
      );
      setModalOpen(true);
    }
  }, [referrals.length, claimedReferralRewards, balance, id, setBalance, setTaskPoints, setClaimedReferralRewards, formatNumber]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  return (
    <>
      {friendsRewards
        .filter((reward) => !claimedReferralRewards.includes(reward.title))
        .map((reward) => {
          const isClaimable = referrals.length >= reward.referralsRequired && 
                            !claimedReferralRewards.includes(reward.title);
          
          return (
            <div 
              key={reward.title} 
              className="w-full rounded-[16px] py-3 flex items-center justify-between space-x-1"
              role="listitem"
            >
              <div className='w-fit pr-2'>
                <div className='flex items-center justify-center bg-[#1f2023] h-[45px] w-[45px] rounded-full p-1'>
                  <img alt="Invite friends icon" src='/invite.svg' className='w-[20px]' />
                </div>
              </div>
              
              <div className="flex h-full flex-1 flex-col justify-center relative">
                <div className='flex w-full flex-col justify-between h-full space-y-1'>
                  <h2 className="text-[15px] text-nowrap line-clamp-1 font-medium">
                    {reward.title}
                  </h2>
                  <span className='flex text-secondary items-center w-fit text-[15px]'>
                    <span>
                      +{formatNumber(reward.bonusAward)} $TFlash
                    </span>
                  </span>
                </div>
              </div>

              <div className='w-fit flex items-center justify-end flex-wrap text-[14px] relative'>
                <RewardButton
                  isClaimable={isClaimable}
                  onClick={() => handleClaim(reward)}
                >
                  Claim
                </RewardButton>
              </div>
            </div>
          );
        })}

      <div 
        className="w-full absolute top-[50px] left-0 right-0 flex justify-center z-50 pointer-events-none select-none"
        aria-hidden={!congrats}
      >
        {congrats && <img src='/congrats.gif' alt="Celebration animation" className="w-[80%]" />}
      </div>

      <Modal isOpen={modalOpen} onClose={closeModal}>
        {modalMessage}
      </Modal>
    </>
  );
};

export default RefRewards;
